<template>
  <div
    ref="root"
    class="p-4 flex flex-col justify-center items-center max-w-2xl w-full"
  >
    <ListSwitcher
      class="tour-led-configs border border-base-300 rounded-lg shadow py-3 pb-4 px-6 mb-5"
      title="Configurations"
      tooltip='Configurations that can be cycled through by pressing a key with the "Led Cycle" action'
      :entries="numberList(kb.config.leds.length)"
      :current="kb.ledConfig"
      :noDelete="kb.config.leds.length <= 1"
      @select="(i) => (kb.ledConfig = i)"
      @insert="kb.addLedConfig"
      @delete="kb.removeLedConfig"
    />

    <Container
      class="w-full flex flex-col justify-center items-center"
      @drop="drop"
      nonDragAreaSelector=".menu-popup"
    >
      <TransitionGroup>
        <Draggable
          class="w-full"
          v-for="(_rule, id) in ledRules ?? []"
          :key="id"
        >
          <div
            class="bg-base-100 border border-base-content/30 rounded-xl shadow mb-4 w-full relative"
            :class="help.selectedLedRule == id ? 'tour-led-rule' : ''"
          >
            <LedRuleEdit v-if="isExpanded(id)" v-model="ledRules[id]" />
            <LedRuleView
              v-else
              :rule="ledRules[id]"
              @click="() => (selectedRule = id)"
            />

            <div
              class="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 z-0"
            >
              <button
                class="btn btn-circle btn-sm btn-outline h-9 w-9 text-neutral bg-base-100 hover:border-neutral hover:bg-neutral"
                @click="removeRule(id)"
              >
                <Icon icon="ic:baseline-delete" @mouseout.stop />
              </button>
            </div>
          </div>
        </Draggable>

        <button
          key="__add_button__"
          class="btn btn-circle btn-sm btn-outline h-9 w-9 text-neutral hover:border-neutral hover:bg-neutral"
          @click="addRule"
        >
          <Icon icon="ic:baseline-add" class="hover:fill-neutral-content" />
        </button>
      </TransitionGroup>
    </Container>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Container, Draggable, DropResult } from "vue-dndrop";
import { useOnClickOutside } from "@/composables";
import { useHelpStore, useKeyboardStore } from "@/stores";
import ListSwitcher from "@/components/utils/ListSwitcher.vue";
import LedRuleEdit from "@/components/leds/LedRuleEdit.vue";
import LedRuleView from "@/components/leds/LedRuleView.vue";
import { LedRule } from "@/keyboard";
import { Icon } from "@iconify/vue";

const kb = useKeyboardStore();
const help = useHelpStore();

const numberList = (length: number) =>
  [...Array(length).keys()].map((l) => l.toString());

const selectedRule = ref<number | null>(null);
const root = ref<HTMLElement | null>(null);

useOnClickOutside(root, () => (selectedRule.value = null));

const ledRules = computed(
  () => help.overrideLedRules ?? kb.config.leds[kb.ledConfig]
);

// run actions only when there are no help overrides
function ifNoHelp<A extends unknown[], R>(fn: (...args: A) => R | undefined) {
  return (...params: A) => {
    if (help.overrideLedRules) return;
    return fn(...params);
  };
}

const isExpanded = (id: number) => {
  if (help.selectedLedRule != null) {
    return help.expandLedRule && id == help.selectedLedRule;
  } else {
    return id == selectedRule.value;
  }
};

const drop = ifNoHelp((result: DropResult) => {
  const from = result.removedIndex;
  const to = result.addedIndex;
  if (from == null || to == null) return;

  const config = kb.config.leds[kb.ledConfig];
  if (config == undefined) return; // missing config
  if (from < 0 || from > config.length - 1) return; // out of range
  if (to < 0 || to > config.length - 1) return; // out of range

  const list = [...config];
  const selectedRef =
    selectedRule.value != null ? list[selectedRule.value] : null;
  list.splice(to, 0, list.splice(from, 1)[0]);

  // Update selected item index to match new list order
  if (selectedRef != null)
    selectedRule.value = list.findIndex((r) => selectedRef === r);

  kb.config.leds[kb.ledConfig] = list;
});

const removeRule = ifNoHelp((id: number) => {
  const config = kb.config.leds[kb.ledConfig];
  if (config == undefined) return;
  kb.config.leds[kb.ledConfig].splice(id, 1);
});

const addRule = ifNoHelp(() => {
  const config = kb.config.leds[kb.ledConfig];
  if (config == undefined) return;

  const rule: LedRule = {
    condition: "Always",
    // condition: { And: [ { Not: { KeyPressed: [0, 2] } }, { Or: ["UsbOn", { Layer: 2 }], }, ], },
    keys: null,
    pattern: {
      phase: { x: 0, y: 0 },
      repeat: "Once",
      transitions: [
        {
          color: [255, 255, 255],
          duration: 0,
          interpolation: "Piecewise",
        },
      ],
    },
  };

  kb.config.leds[kb.ledConfig].push(rule);
});
</script>

<style scoped>
.v-move,
.v-enter-active,
.v-leave-active {
  transition: all 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: scale(0);
}

/* Make the "delete" buttons visible */
.dndrop-container.vertical > .dndrop-draggable-wrapper {
  @apply overflow-visible;
}
</style>
